// Order Business Logic - Professional utility layer
// Centralizes all order-related business rules and status calculations
// Independent of data source (mock/API) and persistence layer

/**
 * Calculates order status based on payment state and product production statuses
 * Following e-commerce workflow:
 * 1. AGUARDANDO PAGAMENTO: Payment not confirmed - seller cannot start preparation
 * 2. EM ANDAMENTO: Payment confirmed, products being prepared
 * 3. PRONTO PARA COLETA / ENVIADO: All products ready
 * 4. CONCLUÍDO: Order fulfilled
 * 5. PARCIALMENTE DEVOLVIDO: Some products returned
 * 6. DEVOLVIDO: All products returned
 * 7. CANCELADO: Order cancelled
 * 
 * @param {Object} order - Order object with payment, products, delivery info
 * @returns {string} Calculated order status
 */
export function calculateOrderStatus(order) {
  if (!order) return 'AGUARDANDO PAGAMENTO';
  
  // If cancelled, keep as CANCELADO regardless of other signals
  if (order.status === 'CANCELADO') {
    return 'CANCELADO';
  }
  
  // Check for returns first
  const returns = order.returns || [];
  if (returns.length > 0) {
    const totalProducts = order.products.length;
    const returnedProductIds = new Set(
      returns.flatMap(r => r.items.map(item => item.productId))
    );
    const returnedCount = returnedProductIds.size;
    
    if (returnedCount === totalProducts) {
      return 'DEVOLVIDO';
    } else if (returnedCount > 0) {
      return 'PARCIALMENTE DEVOLVIDO';
    }
  }
  
  // Preserve explicit concluded state when there are no returns
  if (order.status === 'CONCLUÍDO') {
    return 'CONCLUÍDO';
  }
  
  const isPaid = order.payment?.paidAt != null;
  const products = order.products || [];
  
  // No payment = waiting for payment
  // Seller CANNOT start preparation until payment is confirmed
  if (!isPaid) {
    return 'AGUARDANDO PAGAMENTO';
  }
  
  // Payment confirmed - check production status
  if (products.length === 0) {
    return 'EM ANDAMENTO';
  }
  
  const allReady = products.every(p => p.productionStatus === 'PRONTO');
  
  // All products ready -> ready for delivery/pickup
  if (allReady) {
    const isPickup = order.delivery?.method === 'Retirar na loja';
    return isPickup ? 'PRONTO PARA COLETA' : 'ENVIADO';
  }
  
  // Products being prepared (payment confirmed)
  return 'EM ANDAMENTO';
}

/**
 * Computes the display status for a product inside an order context.
 * Rules:
 * - If product was returned in any return entry => 'DEVOLVIDO'
 * - Else if order is concluded => 'ENTREGUE'
 * - Else use product.productionStatus
 */
export function getProductDisplayStatus(order, product) {
  if (!order || !product) return product?.productionStatus || 'PENDENTE';
  const returnedSet = new Set((order.returns || []).flatMap(r => r.items.map(i => i.productId)));
  if (returnedSet.has(product.id)) return 'DEVOLVIDO';
  if (order.status === 'CONCLUÍDO') return 'ENTREGUE';
  return product.productionStatus;
}

/**
 * Determines the next production status for a product
 * @param {string} currentStatus - Current production status
 * @returns {string} Next status in the workflow
 */
export function getNextProductionStatus(currentStatus) {
  const workflow = {
    'PENDENTE': 'PREPARANDO',
    'PREPARANDO': 'PRONTO',
    'PRONTO': 'PRONTO' // Terminal state
  };
  
  return workflow[currentStatus] || currentStatus;
}

/**
 * Validates if a production status transition is allowed
 * @param {string} from - Current status
 * @param {string} to - Target status
 * @returns {boolean} Whether transition is valid
 */
export function isValidProductionTransition(from, to) {
  const validTransitions = {
    'PENDENTE': ['PREPARANDO'],
    'PREPARANDO': ['PRONTO'],
    'PRONTO': [] // No transitions from ready
  };
  
  return validTransitions[from]?.includes(to) || false;
}

/**
 * Checks if production can be started (payment confirmed)
 * @param {Object} order - Order to check
 * @returns {boolean} Whether production can start
 */
export function canStartProduction(order) {
  if (!order) return false;
  return order.payment?.paidAt != null;
}

/**
 * Checks if an order can be cancelled
 * @param {Object} order - Order to check
 * @returns {boolean} Whether order can be cancelled
 */
export function canCancelOrder(order) {
  if (!order) return false;
  
  const terminalStatuses = ['CONCLUÍDO', 'CANCELADO', 'DEVOLVIDO'];
  return !terminalStatuses.includes(order.status);
}

/**
 * Checks if an order can be marked as complete
 * @param {Object} order - Order to check
 * @returns {boolean} Whether order can be completed
 */
export function canCompleteOrder(order) {
  if (!order) return false;
  
  const readyStatuses = ['PRONTO PARA COLETA', 'ENVIADO'];
  return readyStatuses.includes(order.status);
}

/**
 * Calculates return amounts and determines return status
 * @param {Object} order - Order with returns
 * @returns {{ totalReturned: number, partialReturn: boolean, fullReturn: boolean }}
 */
export function calculateReturnInfo(order) {
  if (!order || !order.returns || order.returns.length === 0) {
    return { totalReturned: 0, partialReturn: false, fullReturn: false };
  }
  
  const totalReturned = order.returns.reduce((sum, ret) => sum + (ret.amount || 0), 0);
  const returnedProductIds = new Set(
    order.returns.flatMap(r => r.items.map(item => item.productId))
  );
  
  const fullReturn = returnedProductIds.size === order.products.length;
  const partialReturn = returnedProductIds.size > 0 && !fullReturn;
  
  return { totalReturned, partialReturn, fullReturn };
}

/**
 * Validates order data structure
 * @param {Object} order - Order to validate
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateOrder(order) {
  const errors = [];
  
  if (!order) {
    errors.push('Order object is required');
    return { valid: false, errors };
  }
  
  if (!order.id) errors.push('Order ID is required');
  if (!order.products || order.products.length === 0) {
    errors.push('Order must have at least one product');
  }
  if (!order.payment) errors.push('Payment information is required');
  if (!order.delivery) errors.push('Delivery information is required');
  
  return {
    valid: errors.length === 0,
    errors
  };
}

export default {
  calculateOrderStatus,
  getNextProductionStatus,
  isValidProductionTransition,
  canStartProduction,
  canCancelOrder,
  canCompleteOrder,
  getProductDisplayStatus,
  calculateReturnInfo,
  validateOrder
};
